/**
 * Visual palette for slice 2 — cool jewel tones over a deep-indigo space.
 * All values are numeric Phaser-friendly hex (0xRRGGBB) except FONT_*.
 */
import type { Primary } from '../beam/tracer';

export const PALETTE = {
  // Primary beams + white — cool jewel tones.
  R: 0xc8324f, // deep ruby
  G: 0x3fa867, // forest green
  B: 0x3b6cd9, // sapphire
  W: 0xf2eed8, // warm cream

  // Secondary glyph colors (switch face dot pairs only — no secondary beams in space).
  Y: 0xd9a040, // amber (R+G)
  M: 0x9344a8, // plum  (R+B)
  C: 0x3aa6a6, // teal  (G+B)

  // World chrome.
  BG: 0x0a0a14,
  GRID_BG: 0x141422,
  GRID_LINE: 0x222238,
  WALL: 0x2a2a3a,
  WALL_HIGHLIGHT: 0x3a3a4e,
  WALL_SHADOW: 0x1a1a26,

  // Slot recess.
  SLOT_FILL: 0x14162d,
  SLOT_FRAME_DIM: 0x2a3a55,
  SLOT_FRAME_EMPTY: 0x2a3a55,

  // Entity bodies.
  PRISM_BODY: 0x3d3a5e,
  PRISM_BODY_DARK: 0x2c294a,
  PRISM_BODY_LIGHT: 0x4f4b76,
  PRISM_STROKE: 0x5a5680,
  PRISM_SHIMMER: 0xdfe6ff,
  SWITCH_DIM: 0x6a4a2a,
  SWITCH_RIM_DIM: 0x2a2638,
  SWITCH_RECESS: 0x1a1a26,
  SWITCH_RECESS_DARKER: 0x10101c,
  LIGHTSOURCE_BODY: 0xf2eed8,
  LIGHTSOURCE_CORE: 0xffffff,

  // Mirror — silver bar tri-stop gradient (top → middle → bottom).
  MIRROR_TOP: 0xe8eef8,
  MIRROR_MID: 0x9aa3b8,
  MIRROR_BOT: 0x3a3f55,
  MIRROR_STROKE: 0x1a1a26,
  MIRROR_HIGHLIGHT: 0xffffff,
  MIRROR_SHADOW: 0x000000,

  // Exit (slice 1 lock; portal uses W for unlocked).
  EXIT_BULKHEAD: 0x1c1c2e,
  EXIT_BULKHEAD_STROKE: 0x2c2c44,
  EXIT_BULKHEAD_SEAM: 0x0a0a14,

  // HUD chrome.
  HUD_TITLE: 0x88c8ff,
  HUD_TEXT: 0xdddddd,
  HUD_DIM: 0x6b7185,
  HINT: 0xffaa66,

  // Atmosphere.
  NEBULA_BLUE: 0x5078dc,
  NEBULA_PURPLE: 0x8c5ac8,
  NEBULA_TEAL: 0x3ca0c8,
  STAR: 0xcfd8ff,
  STAR_BRIGHT: 0xe8eaff,
} as const;

/** CSS color strings for use inside Phaser Text styles (#rrggbb). */
export const FONT = {
  TITLE: '#88c8ff',
  TEXT: '#dddddd',
  DIM: '#6b7185',
  HINT: '#ffaa66',
  GREEN: '#3fa867',
  R: '#c8324f',
  G: '#3fa867',
  B: '#3b6cd9',
} as const;

export const TYPE = {
  SANS: '"Space Grotesk", system-ui, sans-serif',
  MONO: '"JetBrains Mono", ui-monospace, monospace',
} as const;

/** Color used when rendering a beam segment with the given color set. */
export function beamColor(colors: ReadonlySet<Primary>): number {
  if (colors.size === 3) return PALETTE.W;
  if (colors.has('R') && colors.has('G')) return PALETTE.Y;
  if (colors.has('R') && colors.has('B')) return PALETTE.M;
  if (colors.has('G') && colors.has('B')) return PALETTE.C;
  if (colors.has('R')) return PALETTE.R;
  if (colors.has('G')) return PALETTE.G;
  return PALETTE.B;
}

/** Color used for a switch's "lit" body fill given its required colors. */
export function switchLitColor(required: ReadonlyArray<Primary>): number {
  if (required.length === 0) return 0xffcc66; // slice-1 default lit color
  return beamColor(new Set(required));
}
